import React, { useState } from "react";
import axios from "axios";
import { apiurl } from "../config/config";
import Layout, { Container } from "../components/layout";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { SuwasthiLoader } from "../components/loader";

const ManageAttendance = () => {
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("06");
  const [year, setYear] = useState("2025");
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [excelReady, setExcelReady] = useState(false);

  const getDaysInMonth = (month, year) => {
    return new Date(year, parseInt(month), 0).getDate();
  };

  const daysInMonth = Array.from(
    { length: getDaysInMonth(month, year) },
    (_, i) => i + 1
  );

  const fetchAttendance = async () => {
    if (status === "") return;
    setLoading(true);
    setExcelReady(false);
    try {
      const response = await axios.post(`${apiurl}/api/attendance/export`, {
        month,
        year,
        status,
      });
      if (response.data.success) {
        setAttendanceData(response.data.data);
        toast.success("Attendance data fetched successfully!");
        setExcelReady(true);
      } else {
        toast.error("Failed to fetch attendance");
      }
    } catch (error) {
      toast.error("Error fetching attendance data");
    } finally {
      setLoading(false);
    }
  };

  const exportAttendanceExcel = () => {
    if (!excelReady || !Object.keys(attendanceData).length) {
      toast.warning("Fetch attendance data first");
      return;
    }

    const employees = Object.entries(attendanceData);
    const wsData = [];
    const dateHeaders = Array.from(
      { length: getDaysInMonth(month, year) },
      (_, i) => `${i + 1}`
    );
    const headers = [
      "EMPLOYEE CODE",
      "EMPLOYEE NAME",
      "DEPARTMENT",
      "MANAGER NAME",
      ...dateHeaders,
      "PRESENT",
      "HALFDAY",
      "LEAVE",
      "ABSENT",
      "MISSPUNCH",
      "WEEKOFF",
      "PAID LEAVE",
      "TOTAL DAYS",
      "WORKING DAYS",
    ];
    wsData.push(headers);

    for (const [empId, [data]] of employees) {
      const { employeeDetails, attendance } = data;
      const summary = {
        present: 0,
        halfday: 0,
        cl: 0,
        absent: 0,
        mp: 0,
        wo: 0,
        pl: 0,
      };

      const dailyStatus = attendance.map(({ status }) => {
        const raw = (status || "").toLowerCase();
        let short = "NA";
        if (raw === "present") short = "P";
        else if (raw === "absent") short = "A";
        else if (raw === "holiday") short = "H";
        else if (raw === "training") short = "T";
        else if (raw === "lop") short = "LOP";
        else if (raw === "sick") short = "SL";
        else if (raw === "halfday" || raw === "hd") short = "HD";
        else if (raw === "cl" || raw === "leave" || raw === "casual") short = "CL";
        else if (raw === "misspunch" || raw === "mp") short = "MP";
        else if (raw === "wo" || raw === "weekoff") short = "WO";
        else if (raw === "pl" || raw === "paid" || raw === "paidleave" || raw === "pld") short = "PL";
        else if (raw === "unpaid" || raw === "unknown" || raw === "other" || raw === "lop" || raw === "ul") short = "UP";

        if (short === "P") summary.present++;
        else if (short === "HD") summary.halfday++;
        else if (short === "CL") summary.cl++;
        else if (short === "A") summary.absent++;
        else if (short === "MP") summary.mp++;
        else if (short === "WO") summary.wo++;
        else if (short === "PL") summary.pl++;
        

        return short;
      });

      wsData.push([
        `SUW${empId}`,
        employeeDetails.name,
        employeeDetails.department,
        employeeDetails.manager,
        ...dailyStatus,
        summary.present,
        summary.halfday,
        summary.cl,
        summary.absent,
        summary.mp,
        summary.wo,
        summary.pl,
        getDaysInMonth(month, year),
        26,
      ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = headers.map(() => ({ wch: 12 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Attendance-${month}-${year}`);
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `attendance_${month}_${year}.xlsx`
    );
  };

  //   const getStatusColor = (status) => {
  //     const s = status?.toLowerCase();

  //     switch (s) {
  //       case "present":
  //       case "p":
  //         return "bg-green-200 text-green-800";

  //       case "absent":
  //       case "a":
  //         return "bg-red-200 text-red-800";

  //       case "halfday":
  //       case "hd":
  //         return "bg-yellow-200 text-yellow-800";

  //       case "misspunch":
  //       case "mp":
  //         return "bg-purple-200 text-purple-800";

  //       case "leave":
  //       case "pl":
  //       case "cl":
  //         return "bg-cyan-200 text-cyan-800";

  //       case "weekoff":
  //       case "wo":
  //         return "bg-blue-200 text-blue-800";

  //       default:
  //         return "bg-gray-200 text-gray-800";
  //     }
  //   };
  const getStatusColor = (status) => {
    if (!status) return "bg-gray-200 text-gray-700";

    switch (status.toLowerCase()) {
      case "present":
      case "p":
        return "bg-[#4CAF50] text-white"; // Green
      case "absent":
      case "Absent":
      case "a":
        return "bg-[#F44336] text-white"; // Red
      case "leave":
      case "sick":  
      case "casual":
      case "cl":
        return "bg-[#2196F3] text-white"; // Blue (Casual Leave)
      case "pl":
      case "paidleave":
      case "paid":
      case "pld":
        return "bg-[#3F51B5] text-white"; // Indigo (Paid Leave)
      case "ml":
        return "bg-[#FF9800] text-white"; // Orange (Medical Leave)
      case "maternity":
      case "m":
        return "bg-[#F8BBD0] text-black"; // Light Pink
      case "halfday":
      case "hd":
        return "bg-[#FFC107] text-black"; // Yellow (Half Day)
      case "holiday":
      case "h":
        return "bg-[#FFEB3B] text-black"; // Yellow (Holiday)
      case "weekoff":
      case "wo":
        return "bg-[#9E9E9E] text-black"; // Grey (Week Off)
      case "misspunch":
        return "bg-[#FFB74D] text-black"; // Light Orange
      case "unpaid":
      case "other":
      case "unknown":
      case "ul":
      case "lop":
        return "bg-[#FF5722] text-white"; // Deep Orange (Loss of Pay)
      case "study":
      case "sl":
        return "bg-[#00BCD4] text-white"; // Cyan
      case "bereavement":
      case "bl":
        return "bg-[#6D4C41] text-white"; // Brown
      case "workfromhome":
      case "wfh":
        return "bg-[#009688] text-white"; // Teal
      case "onduty":
      case "od":
        return "bg-[#673AB7] text-white"; // Purple
      case "training":
      case "trn":
        return "bg-[#90CAF9] text-black"; // Light Blue
      case "earlyout":
      case "eod":
        return "bg-[#FFCDD2] text-black"; // Light Red
      default:
        return "bg-gray-200 text-gray-700"; // Fallback
    }
  };
  const getShortStatusCode = (status) => {
    switch (status.toUpperCase()) {
      case "PRESENT":
        return "P";
      case "HOLIDAY":
        return "H";
      case "ABSENT":
        return "A";
      case "WEEKOFF":
      case "WO":
        return "WO";
      case "LEAVE":
      case "CL":
      case "PL":
      case "PAID":
        return "PL";
      case "HALFDAY":
      case "HD":
        return "HD";
      case "MISSPUNCH":
      case "MP":
        return "MP";
      case "CASUAL":
        return "CL";
      case "UNPAID":
      case "OTHER":
      case "UNKNOWN":
        return "UP";
      case "SICK":
        return "SL";
        case"TRAINING":
        return "T";
      default:
        return "-";
    }
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="flex flex-wrap gap-4 items-center mb-6">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-400 px-4 py-2 rounded"
          >
            <option value="">Select Status</option>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-400 px-4 py-2 rounded"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border border-gray-400 px-4 py-2 rounded"
          >
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
          </select>

          <button
            onClick={fetchAttendance}
            disabled={!status || loading}
            className={`px-4 py-2 rounded text-white ${
              !status || loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {loading ? "Fetching..." : "Fetch Attendance"}
          </button>

          <button
            onClick={exportAttendanceExcel}
            disabled={!excelReady}
            className={`px-4 py-2 rounded text-white ${
              excelReady
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Download Excel
          </button>
        </div>

        {loading && <SuwasthiLoader />}

        {!loading && Object.keys(attendanceData).length > 0 && (
          <div className="bg-white p-4 border border-gray-300 rounded shadow mb-4">
            ✅ <strong>{Object.keys(attendanceData).length}</strong> employees
            fetched. Ready for download.
          </div>
        )}

        <div className="bg-white p-3 rounded border border-gray-300 mb-2 text-sm">
          <strong>Attendance Codes:</strong>
          <br />

          <div className="flex flex-wrap gap-4 items-center mb-4 text-sm mt-2">
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#4CAF50] text-white flex items-center justify-center text-sm">
                P
              </span>
              Present
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#FFC107] text-black flex items-center justify-center text-sm">
                HD
              </span>
              Half Day
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#3F51B5] text-white flex items-center justify-center text-sm">
                PL
              </span>
              Paid Leave
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#9E9E9E] text-black flex items-center justify-center text-sm">
                WO
              </span>
              Week Off
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#FFB74D] text-black flex items-center justify-center text-sm">
                MP
              </span>
              Miss Punch
            </div>
            <div className="flex items-center gap-1">
              <span className="w-6 h-6 rounded-full bg-[#F44336] text-white flex items-center justify-center text-sm">
                A
              </span>
              Absent
            </div>
          </div>
        </div>

        {Object.keys(attendanceData).length > 0 && (
          <div className="overflow-auto bg-gray-100">
            <table className="table-auto border border-gray-300 text-sm bg-white min-w-[1000px]">
              <thead className="bg-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="border px-4 py-2">Name</th>
                  <th className="border px-4 py-2">Employee ID</th>
                  <th className="border px-4 py-2">Manager</th>
                  {daysInMonth.map((day) => (
                    <th key={day} className="border px-2 py-1 text-center w-10">
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(attendanceData).map(([empId, records]) => {
                  const emp = records[0].employeeDetails;
                  const attMap = Object.fromEntries(
                    records[0].attendance.map((att) => [
                      new Date(att.date).getDate(),
                      att.status.toUpperCase(),
                    ])
                  );

                  return (
                    <tr key={empId} className="hover:bg-gray-50">
                      <td className="border px-4 py-2 font-medium capitalize">
                        {emp.name}
                      </td>
                      <td className="border px-4 py-2 text-center capitalize">
                        {empId}
                      </td>
                      <td className="border px-4 py-2 capitalize">
                        {emp.manager}
                      </td>
                      {daysInMonth.map((day) => {
                        const status = attMap[day] || "-";
                        return (
                          <td
                            key={day}
                            className="border text-center px-1 py-1"
                          >
                            {/* <span className={`text-xs font-bold inline-block w-6 h-6 leading-6 rounded-full ${getStatusColor(status)}`}>
                              {status.charAt(0)}
                            </span> */}
                            <span
                              className={`text-xs  inline-block w-6 h-6 leading-6 rounded-full ${getStatusColor(
                                status
                              )}`}
                            >
                              {getShortStatusCode(status)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default ManageAttendance;
